/**
 * SONGZIO storefront price presentation.
 *
 * Shopify and Global-e continue to own monetary amounts, currency conversion,
 * rounding and payment data. Only the visible text of storefront price elements
 * is adjusted: decimal fractions consisting of exactly two zeroes are hidden.
 *
 * Cart pages, cart drawers and the checkout are deliberately excluded.
 * MutationObserver also handles asynchronously replaced price markup and
 * currency/market updates without changing underlying data attributes.
 */
(function () {
  'use strict';

  var currentPath = window.location.pathname;
  if (/(^|\/)cart(?:\/|$)/i.test(currentPath) || /(^|\/)checkouts?(?:\/|$)/i.test(currentPath)) {
    return;
  }

  var priceSelector = [
    'product-price .price',
    'product-price .compare-at-price',
    '.price-item--regular',
    '.price-item--sale',
    '.price-per-item__text',
    '.volume-pricing-info__price',
    '.unit-price',
    '.product-tabs__product-price',
    '.variant-item__mobile-price',
    '.variant-item__total-price',
    '.quick-order-list-total__price',
    '.quick-order-list .price',
    '.quick-order-list .compare-at-price',
    '.price-facet__values',
    '.filter-remove-button',
    '.filter-remove-buttons',
    '.price',
    '.compare-at-price',
    '.ge-price',
    '.ge-price-value',
    '[data-ge-price]',
    '[data-globale-price]',
    '#FreeShippingBanner',
    '.ge-free-shipping-msg'
  ].join(',');

  var ignoreSelector = [
    '.cart-drawer',
    'cart-drawer-component',
    '.cart-page',
    '.cart-items',
    '.cart-summary',
    'cart-items-component',
    'cart-summary-component',
    '.cart-notification',
    '[data-cart-drawer]',
    '[data-testid="cart-drawer"]',
    '.visually-hidden',
    '[aria-hidden="true"]',
    'script',
    'style',
    'template',
    'noscript',
    'input',
    'textarea',
    'select'
  ].join(',');

  var pending = new Set();
  var isScheduled = false;

  /**
   * Preserve commas, thousands separators, currency codes and actual cents.
   * Handles both 1,250.00 and 1.250,00, but not 450.50 / 450,25.
   */
  function removeZeroDecimalFraction(originalText) {
    return originalText.replace(/(^|[^\d])(\d{1,3}(?:[.,'’ \u00a0\u202f]\d{3})+|\d+)[.,]00(?!\d)/g, function (match, prefix, wholeAmount) {
      return prefix + wholeAmount;
    });
  }

  function isIgnored(element) {
    return element.closest(ignoreSelector) !== null;
  }

  /**
   * Change only Text nodes, never innerHTML, price attributes or hidden fields.
   * Excludes accessible hidden labels and all cart display containers.
   */
  function adjustElement(element) {
    if (!element.isConnected || isIgnored(element)) {
      return;
    }

    var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
    var textNode;

    while ((textNode = walker.nextNode())) {
      if (!textNode.nodeValue || textNode.nodeValue.indexOf('00') === -1) {
        continue;
      }

      var parent = textNode.parentElement;
      if (!parent || isIgnored(parent)) {
        continue;
      }

      var nextText = removeZeroDecimalFraction(textNode.nodeValue);
      if (nextText !== textNode.nodeValue) {
        textNode.nodeValue = nextText;
      }
    }
  }

  function queueElement(element) {
    if (!element || !element.isConnected) {
      return;
    }

    if (element.matches(priceSelector)) {
      pending.add(element);
    }

    var priceElements = element.querySelectorAll(priceSelector);
    for (var index = 0; index < priceElements.length; index += 1) {
      pending.add(priceElements[index]);
    }

    if (!isScheduled && pending.size > 0) {
      isScheduled = true;
      window.requestAnimationFrame(flushPending);
    }
  }

  function queueTextParent(parent) {
    if (!parent) {
      return;
    }
    var priceElement = parent.closest(priceSelector);
    if (priceElement && priceElement.isConnected) {
      pending.add(priceElement);
      if (!isScheduled) {
        isScheduled = true;
        window.requestAnimationFrame(flushPending);
      }
    }
  }

  function flushPending() {
    isScheduled = false;
    var elements = Array.from(pending);
    pending.clear();

    for (var index = 0; index < elements.length; index += 1) {
      adjustElement(elements[index]);
    }
  }

  function start() {
    if (!document.body) {
      return;
    }

    queueElement(document.body);

    var observer = new MutationObserver(function (mutations) {
      for (var index = 0; index < mutations.length; index += 1) {
        var mutation = mutations[index];

        if (mutation.type === 'characterData') {
          queueTextParent(mutation.target.parentElement);
          continue;
        }

        queueTextParent(mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement);

        for (var childIndex = 0; childIndex < mutation.addedNodes.length; childIndex += 1) {
          var addedNode = mutation.addedNodes[childIndex];
          if (addedNode.nodeType === Node.ELEMENT_NODE) {
            queueElement(addedNode);
          } else if (addedNode.nodeType === Node.TEXT_NODE) {
            queueTextParent(addedNode.parentElement);
          }
        }
      }
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true
    });

    document.addEventListener('shopify:section:load', function (event) {
      if (event.target instanceof Element) {
        queueElement(event.target);
      }
    });
  }

  /**
   * Global-e may insert or replace its shipping announcement after the
   * initial Shopify render.  Normalizing its text immediately in the
   * mutation microtask (rather than at the next animation frame) prevents
   * the replacement value from remaining visible with two trailing zeroes.
   * Actual thresholds and data attributes are left unchanged.
   */
  function normalizeShippingBanner() {
    var messages = document.querySelectorAll('#FreeShippingBanner, .ge-free-shipping-msg');

    for (var index = 0; index < messages.length; index += 1) {
      var message = messages[index];
      var walker = document.createTreeWalker(message, NodeFilter.SHOW_TEXT, null);
      var textNode;

      while ((textNode = walker.nextNode())) {
        if (!textNode.nodeValue || textNode.nodeValue.indexOf('00') === -1) {
          continue;
        }

        var normalizedText = removeZeroDecimalFraction(textNode.nodeValue);
        if (normalizedText !== textNode.nodeValue) {
          textNode.nodeValue = normalizedText;
        }
      }
    }
  }

  function hasShippingBannerChange(mutation) {
    var bannerSelector = '#FreeShippingBanner, .ge-free-shipping-msg, .ge-free-shipping-container';
    var target = mutation.target.nodeType === Node.ELEMENT_NODE
      ? mutation.target
      : mutation.target.parentElement;

    if (target && target.closest && target.closest(bannerSelector)) {
      return true;
    }

    for (var index = 0; index < mutation.addedNodes.length; index += 1) {
      var addedNode = mutation.addedNodes[index];
      if (addedNode.nodeType === Node.ELEMENT_NODE && (
        addedNode.matches(bannerSelector) || addedNode.querySelector(bannerSelector)
      )) {
        return true;
      }
    }

    return false;
  }

  function startShippingBannerObserver() {
    normalizeShippingBanner();

    var bannerObserver = new MutationObserver(function (mutations) {
      for (var index = 0; index < mutations.length; index += 1) {
        if (hasShippingBannerChange(mutations[index])) {
          normalizeShippingBanner();
          return;
        }
      }
    });

    bannerObserver.observe(document.documentElement, {
      subtree: true,
      childList: true,
      characterData: true
    });
  }

  startShippingBannerObserver();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}());
